import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { IS_T3_WEBSITE } from '../../constants/environment';
import LightBox from '../LightBox';
import VehicleInfo from '../VehicleInfo';
import VehicleStatusInfo from '../VehicleStatusInfo';
import {
  Header,
  Image,
  VehicleHeaderWrapper,
  HeadingContainer,
  HeaderTextContainer,
  FlickFusionWrapper,
  BackgroundImage,
  FallbackImage,
  TabletSubHeader,
} from './VehicleHeader.style';
import usePrimaryImageUrl from '../../hooks/usePrimaryImageUrl';
import useOptimizePrimaryImage from '../../hooks/useOptimizePrimaryImage';
import isFallback from '../../utils/isFallback';
import usePalette from '../../hooks/usePalette';
import type { VehicleInfo as VehicleInfoTypes, Impel360ViewCTA } from '../../types/index';
import Impel360CTA from '../Impel360CTA';

interface HeaderNewProps {
  vehicleData?: VehicleInfoTypes;
  cta360View: Impel360ViewCTA;
  isTablet: boolean;
  isMobile: boolean;
}

const HeaderNewWrapper: React.FC<HeaderNewProps> = ({
  vehicleData,
  cta360View,
  isTablet,
  isMobile,
}) => {
  let primaryImageUrl: string = usePrimaryImageUrl(vehicleData);

  const { backgroundColor, darkenedBackgroundColor, textColor } = usePalette(vehicleData);
  const vehicleType = vehicleData?.vehicleType;
  const modelName = vehicleData?.modelName;
  const nonAudi = vehicleType === 'non-audi';
  const isUsed = vehicleType === 'used';
  const isNew = vehicleType === 'new';
  primaryImageUrl = useOptimizePrimaryImage(primaryImageUrl, isNew);
  const fallback = primaryImageUrl ? isFallback(primaryImageUrl) : false;

  const images = vehicleData?.vehicleMedia.mediaImages;
  const dealerImages = vehicleData?.vehicleDealerImages;
  const dealerPrimaryImage = dealerImages?.[0];
  const vinCode = vehicleData?.vin;

  const { is360ViewEnabled, cta360ViewButtonLabel } = cta360View;

  const imagesToRender = useMemo(() => {
    if (IS_T3_WEBSITE && dealerImages)
      return dealerImages.map((image, index) => ({
        url: image,
        imageType: index.toString(),
        config: index.toString(),
      }));
    return images;
  }, [dealerImages, images]);

  const mainImage = useMemo(() => {
    if (IS_T3_WEBSITE && dealerPrimaryImage) return dealerPrimaryImage;
    return primaryImageUrl;
  }, [dealerPrimaryImage, primaryImageUrl]);

  const flickFusionDiv = useMemo(() => {
    return `
      <div
      data-debug="CHAD"
      data-vendor="flickfusion"
      id="ff_link"
      ff_vin="${vinCode || ''}"
      verified
    ></div>
    `;
  }, [vinCode]);

  const sanitizedHTML = DOMPurify.sanitize(flickFusionDiv, {
    ADD_ATTR: ['ff_client', 'ff_vin', 'verified'],
  });
  return (
    <Header
      color={textColor}
      fallback={fallback}
      backgroundColor={backgroundColor}
      darkenedBackgroundColor={darkenedBackgroundColor}
      nonAudi={nonAudi}
      data-testid="header"
    >
      <VehicleHeaderWrapper fallback={fallback}>
        <HeadingContainer backgroundColor={backgroundColor} fallback={fallback}>
          <HeaderTextContainer
            nonAudi={nonAudi}
            isUsed={isUsed}
            fallback={fallback}
            data-vehicle-status={
              vehicleData?.vehicleType === 'new' && vehicleData.isAvailableNow
                ? 'null'
                : vehicleData?.vehicleOrderStatus || 'Unavailable'
            }
            data-vehicle-sale-type={
              vehicleData?.vehicleType === 'new' && vehicleData.isAvailableNow
                ? 'available-now'
                : vehicleData?.saleOrderType || vehicleData?.vehicleInventoryType
            }
            data-testid="vehicle-info"
          >
            <VehicleInfo />
          </HeaderTextContainer>
          <VehicleStatusInfo />
          <Impel360CTA
            isVisible={is360ViewEnabled}
            label={cta360ViewButtonLabel}
            color={textColor}
            isNew={isNew}
            isT3={IS_T3_WEBSITE}
          />
          {IS_T3_WEBSITE && (
            <FlickFusionWrapper dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />
          )}
          {mainImage && fallback
            ? !isTablet && (
                <BackgroundImage
                  image={mainImage}
                  fallback={fallback}
                  data-testid="primary-image"
                />
              )
            : !isTablet && <Image src={mainImage} data-testid="primary-image" alt={modelName} />}
          {isMobile && fallback && (
            <FallbackImage src={mainImage} data-testid="primary-image" alt={modelName} />
          )}
        </HeadingContainer>
      </VehicleHeaderWrapper>

      {isTablet && (
        <TabletSubHeader>
          {mainImage && <Image src={mainImage} alt={modelName} />}
          <LightBox
            images={imagesToRender}
            header={mainImage}
            modelName={modelName}
            nonAudi={nonAudi}
            used={isUsed}
          />
        </TabletSubHeader>
      )}

      {!isTablet && (
        <LightBox
          images={imagesToRender}
          header={primaryImageUrl}
          modelName={modelName}
          nonAudi={nonAudi}
          used={isUsed}
        />
      )}
    </Header>
  );
};

export default HeaderNewWrapper;
